import React, {useMemo, useState} from 'react'
import {CopyText, Button, Modal, Icon} from '@therms/atalaya'
import {
  Alerts,
  CsvImportMapper,
  CsvImportSteps,
  useRPCContext,
} from '@therms/react-modules'
import {RPCDocs} from '@therms/rpc-gateway'
import {
  ItemColumnMapping,
  PersonColumnMapping,
  VehicleColumnMapping,
} from '@src/modules/Records/shared/BulkImportRecords/BulkImportRecordsColumnMapping.ts'
import {mapImportDataToRecordFields} from '@src/modules/Records/utils/mapImportDataToRecordFields.ts'
import {IoMdDownload} from 'react-icons/io'

interface BulkImportRecordsProps {
  onComplete?: () => void
  onStepChange?: (step: CsvImportSteps) => void
  recordType:
    | RPCDocs.Records.CreateRecord.v1.Type
    | RPCDocs.Records.BulkCreateRecords.v1.Type
}

export function BulkImportRecords({
  onComplete,
  onStepChange,
  recordType,
}: BulkImportRecordsProps) {
  const {rpcService} = useRPCContext()
  const [step, setStep] = useState<CsvImportSteps>('pick-file')
  const [data, setData] = useState<
    RPCDocs.Records.BulkCreateRecords.v1.Fields[]
  >([])
  const [processing, setProcessing] = useState(false)
  const [processingErrored, setProcessingErrored] = useState<
    RPCDocs.Records.BulkCreateRecords.v1.Errored[]
  >([])
  const [skippedDuplicates, setSkippedDuplicates] = useState<number>(0)
  const [documentKey, setDocumentKey] = useState<string | undefined>()

  const handleOnComplete = (
    data: Array<Record<string, string | string[] | undefined>>,
  ) => {
    setData(data)
    setStep('info')
  }

  const runImport = async () => {
    let errors = 0

    const recordsToImport = mapImportDataToRecordFields({
      importData: data,
      recordType,
    })
    setProcessingErrored([])
    setSkippedDuplicates(0)
    setProcessing(true)

    try {
      await rpcService.Records.BulkCreateRecords.v1
        .call({
          documentKey,
          records: recordsToImport,
        })
        .then((response) => {
          if (response.data.errored.length) {
            setProcessingErrored(response.data.errored)
            errors++
            Alerts.error(
              `Import completed with ${response.data.errored.length} errored records. Please review the errors and fix the data before re-importing.`,
            )
          }

          if (response.data.skippedDuplicates > 0) {
            setSkippedDuplicates(response.data.skippedDuplicates)
            errors++
          }

          if (!errors && !skippedDuplicates && !processingErrored.length) {
            onComplete?.()

            Alerts.success(`Successfully imported ${data.length} records.`)
          }
        })
    } catch (e: any) {
      setProcessingErrored([e.message])
      console.error('Error processing row', e)
    }

    setProcessing(false)
  }

  const recordTypeMapping = useMemo(() => {
    if (recordType === RPCDocs.Records.BulkCreateRecords.v1.Type.Item) {
      return ItemColumnMapping
    }

    if (recordType === RPCDocs.Records.BulkCreateRecords.v1.Type.Person) {
      return PersonColumnMapping
    }

    if (recordType === RPCDocs.Records.BulkCreateRecords.v1.Type.Vehicle) {
      return VehicleColumnMapping
    }

    return []
  }, [recordType])

  if (step === 'pick-file')
    return (
      <div>
        <CsvImportMapper
          allowInlineEditing={false}
          columnMapping={recordTypeMapping}
          onComplete={handleOnComplete}
          onStepChange={onStepChange}
          setDocumentKey={setDocumentKey}
        />

        <div className="pt-sm flex w-full justify-center">
          <a
            href="/ops/public/VehicleExample.csv"
            download="VehicleExample.csv"
            className="hover:cursor-pointer"
          >
            <Button subtle size="xs">
              <Icon Icon={IoMdDownload} size="sm" />
              <span className="pl-xxs">Download Example CSV</span>
            </Button>
          </a>
        </div>
      </div>
    )

  if (step === 'info') {
    return (
      <div>
        <div className="flex justify-center">
          <Button
            className="mt-base"
            disabled={processing}
            onClick={() => {
              setStep('process')
              runImport()
            }}
            variant="main"
          >
            Run Import
          </Button>
        </div>
      </div>
    )
  }

  if (step === 'process') {
    return (
      <div className="flex w-full justify-center text-center">
        {processing ? <div>Importing {data.length} Records...</div> : null}

        {!processing &&
        (processingErrored?.length > 0 || skippedDuplicates > 0) ? (
          <div className="">
            <p>Processed {data.length} Records</p>

            {processingErrored.length > 0 && (
              <>
                <h3 className="text-critical">
                  Errors {processingErrored.length}
                </h3>
                <div>
                  <CopyText value={JSON.stringify(processingErrored)}>
                    Copy Errors to Clipboard
                  </CopyText>
                </div>

                <ul>
                  {processingErrored.map((error, index) => (
                    <>
                      {error.lineNumber ? (
                        <li key={index}>
                          Row {error.lineNumber}: {error.error}
                          <pre>{JSON.stringify(error.error, null, 2)}</pre>
                        </li>
                      ) : (
                        <pre>{JSON.stringify(error, null, 2)}</pre>
                      )}
                    </>
                  ))}
                </ul>
              </>
            )}

            {skippedDuplicates > 0 && (
              <h3 className="text-critical">
                {skippedDuplicates} Duplicate Records skipped
              </h3>
            )}
          </div>
        ) : (
          <div>
            <p className="text-info">Records have been processed.</p>
          </div>
        )}
      </div>
    )
  }
}

export function BulkImportRecordsModal({
  closeHandler,
  onComplete,
  ...props
}: {
  closeHandler: () => void
} & BulkImportRecordsProps) {
  const [modalWidth, setModalWidth] = useState('sm')

  return (
    <Modal
      closeHandler={() => closeHandler()}
      header="Import Records"
      width={modalWidth}
    >
      <BulkImportRecords
        onStepChange={(step) => {
          if (step === 'pick-file') setModalWidth('sm')
          if (step === 'select-mapping') setModalWidth('md')
          if (step === 'finalize') setModalWidth('xl')
          if (step === 'info') setModalWidth('sm')
        }}
        onComplete={() => {
          onComplete?.()
          closeHandler()
        }}
        {...props}
      />
    </Modal>
  )
}
